import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import AppTextInput from "../../app/components/AppTextInput";
import { FormProvider, useForm } from "react-hook-form";
import AppAutocomplete from "../../app/components/AppAutocomplete";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { OrganiserDetailsValidationSchemas } from "./common/eventApprovalValidationSchemas";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { gePcsEventByIdAsync, updatePcsEventAsync } from "../../app/slices/pcsEventSlice";
import { toast } from "react-toastify";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NEXT, MESSAGE_PROCESSING_DETAILS, MESSAGE_SAVE_AND_CONTINUE } from "../../app/utils/constant";
import { MapEventOrganiserTypesToAutocompleteItems, MapEventOrganisingInstitutionTypeToAutocompleteItems } from "../../app/utils/convertStaticDataToAutocompleteItems";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { useEffect, useState } from "react";
import AppTextareaInput from "../../app/components/AppTextareaInput";
import { EventOrganiserTypeEnum } from "../../app/enums/eventOrganiserTypeEnum";
import { OrganisingInstitutionTypeEnum } from "../../app/enums/organisingInstitutionTypeEnum";
import AppRadioButtonsGroup from "../../app/components/AppRadioButtonsGroup";
import { yesNoArray } from "../../app/utils/util";
import { YesNoEnum } from "../../app/enums/yesNoEnum";
import OrganiserDetailsHeader from "./common/OrganiserDetailsHeader";

const { OrganiserType, OrganisingInstitutionType, OrganisingInstitutionOtherType, OrganisingInstitution, LeadOrganiserFirstName,
    LeadOrganiserSurname, LeadOrganiserIMCNumber, LeadOrganiserMobile, LeadOrganiserEmail, LeadOrganiserWorkplaceAddress,
    AreYouEventOrganiser, AdministrationOrganiserDetailsFirstName, AdministrationOrganiserSurname, AdministrationOrganiserMobile,
    AdministrationOrganiserEmail, AdministrationOrganiserWorkplaceAddress
} = EventsFormModel.organiserDetailsFormField;

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function OrganiserDetails({ isSpaEditabe, handleNext, handleBack }: Props) {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(OrganiserDetailsValidationSchemas)
    });
    const formObj = methods.watch();
    const { control, reset, trigger, setValue, getValues, watch, formState: { isDirty } } = methods;

    const { pcsEventDetails, getPcsEventStatus, updatePcsEventStatus } = useAppSelector(state => state.pcsEvent);
    const { organisingInstitutionTypes, eventOrganiserTypes } = useAppSelector(state => state.ReferenceData);
    const { personalDetails } = useAppSelector(state => state.personalDetail);

    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));

    const MapToPcsEvent = (values: any) => {
        const imcNumber = values.OrganiserType === EventOrganiserTypeEnum.SurgicalMedicalLeadOrganiser ? values.LeadOrganiserIMCNumber : '';
        const pcsEventDtoObj: PcsEventDto = {
            ...pcsEventDetails,
            organiserTypeId: values.OrganiserType,
            organiserInstitutionTypeId: values.OrganisingInstitutionType,
            organiserInstitutionOtherType: values.OrganisingInstitutionOtherType,
            organiserInstitution: values.OrganisingInstitution,

            leadOrganiserFirstname: values.LeadOrganiserFirstName,
            leadOrganiserSurname: values.LeadOrganiserSurname,
            leadOrganiserImc: imcNumber,
            leadOrganiserMobile: values.LeadOrganiserMobile,
            leadOrganiserEmail: values.LeadOrganiserEmail,
            leadOrganiserAddress: values.LeadOrganiserWorkplaceAddress,
            areYouTheEventOrganiserAdministrator: values.AreYouEventOrganiser === YesNoEnum.YES,
            adminOrganiserFirstname: values.AdministrationOrganiserDetailsFirstName,
            adminOrganiserSurname: values.AdministrationOrganiserSurname,
            adminOrganiserMobile: values.AdministrationOrganiserMobile,
            adminOrganiserEmail: values.AdministrationOrganiserEmail,
            adminOrganiserAddress: values.AdministrationOrganiserWorkplaceAddress
        }

        return pcsEventDtoObj;
    }

    useEffect(() => {
        if (pcsEventDetails !== null && eventId !== null && eventId !== '') {
            const isOrganiser = pcsEventDetails?.areYouTheEventOrganiserAdministrator;
            reset({
                [OrganiserType.name]: pcsEventDetails?.organiserTypeId,
                [OrganisingInstitutionType.name]: pcsEventDetails?.organiserInstitutionTypeId,
                [OrganisingInstitutionOtherType.name]: pcsEventDetails?.organiserInstitutionOtherType,
                [OrganisingInstitution.name]: pcsEventDetails?.organiserInstitution,
                [LeadOrganiserFirstName.name]: pcsEventDetails?.leadOrganiserFirstname,
                [LeadOrganiserSurname.name]: pcsEventDetails?.leadOrganiserSurname,
                [LeadOrganiserIMCNumber.name]: pcsEventDetails?.leadOrganiserImc ?? '',
                [LeadOrganiserMobile.name]: pcsEventDetails?.leadOrganiserMobile,
                [LeadOrganiserEmail.name]: pcsEventDetails?.leadOrganiserEmail,
                [LeadOrganiserWorkplaceAddress.name]: pcsEventDetails?.leadOrganiserAddress,
                [AdministrationOrganiserDetailsFirstName.name]:
                    isOrganiser === true ? personalDetails?.firstName : pcsEventDetails?.adminOrganiserFirstname,
                [AdministrationOrganiserSurname.name]:
                    isOrganiser === true ? personalDetails?.lastName : pcsEventDetails?.adminOrganiserSurname,
                [AdministrationOrganiserMobile.name]:
                    isOrganiser === true ? personalDetails?.mobile : pcsEventDetails?.adminOrganiserMobile,
                [AdministrationOrganiserEmail.name]:
                    isOrganiser === true ? personalDetails?.preferredEmail : pcsEventDetails?.adminOrganiserEmail,
                [AdministrationOrganiserWorkplaceAddress.name]:
                    isOrganiser === true ? personalDetails?.postalAddress : pcsEventDetails?.adminOrganiserAddress,
                [AreYouEventOrganiser.name]: pcsEventDetails?.areYouTheEventOrganiserAdministrator === true ? YesNoEnum.YES : YesNoEnum.NO
            });
        }
    }, [reset, pcsEventDetails, eventId, personalDetails?.firstName, personalDetails?.lastName, personalDetails?.mobile, personalDetails?.preferredEmail, personalDetails?.postalAddress]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === OrganiserType.name && type === 'change') {
                setValue(LeadOrganiserIMCNumber.name, '');
            }

            if (name === OrganisingInstitutionType.name && type === 'change') {
                setValue(OrganisingInstitutionOtherType.name, '');
            }

            if (name === AreYouEventOrganiser.name && type === 'change') {
                if (value.AreYouEventOrganiser === YesNoEnum.NO) {
                    setValue(AdministrationOrganiserDetailsFirstName.name, '');
                    setValue(AdministrationOrganiserSurname.name, '');
                    setValue(AdministrationOrganiserMobile.name, '');
                    setValue(AdministrationOrganiserEmail.name, '');
                    setValue(AdministrationOrganiserWorkplaceAddress.name, '');
                }
                else if (value.AreYouEventOrganiser === YesNoEnum.YES) {
                    setValue(AdministrationOrganiserDetailsFirstName.name, personalDetails?.firstName);
                    setValue(AdministrationOrganiserSurname.name, personalDetails?.lastName);
                    setValue(AdministrationOrganiserMobile.name, personalDetails?.mobile);
                    setValue(AdministrationOrganiserEmail.name, personalDetails?.preferredEmail);
                    setValue(AdministrationOrganiserWorkplaceAddress.name, personalDetails?.postalAddress);
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [personalDetails?.firstName, personalDetails?.lastName, personalDetails?.mobile, personalDetails?.postalAddress, personalDetails?.preferredEmail, setValue, watch]);

    const dispatch = useAppDispatch();

    const saveDetails = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext('OrganiserDetails'), 'ValidationBlocked', { step: 'organiser' });

        if (isStepValid && isDirty === true) {
            dispatch(updatePcsEventAsync(MapToPcsEvent(formObj))).then((response) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    toast.success(MESSAGE_DETAILS_SAVED);
                    pcsEventDetails?.eventId && dispatch(gePcsEventByIdAsync(pcsEventDetails?.eventId));
                    handleNext();
                }
            });
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    usePortalScreen(portalScreenContext('OrganiserDetails'), undefined, !(getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending));

    if (getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>

        <OrganiserDetailsHeader />

        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ ml: '2%' }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={OrganiserType.name} label={OrganiserType.label}
                        items={MapEventOrganiserTypesToAutocompleteItems(eventOrganiserTypes ?? [])}
                        disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={OrganisingInstitutionType.name} label={OrganisingInstitutionType.label}
                        items={MapEventOrganisingInstitutionTypeToAutocompleteItems(organisingInstitutionTypes ?? [])}
                        disabled={isSpaEditabe} />
                </Grid>

                {getValues(OrganisingInstitutionType.name as keyof PcsEventDto) === OrganisingInstitutionTypeEnum.Other &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={OrganisingInstitutionOtherType.name} label={OrganisingInstitutionOtherType.label}
                            maxStringCount={OrganisingInstitutionOtherType.maxStringCount} disabled={isSpaEditabe} />
                    </Grid>
                }

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={OrganisingInstitution.name} label={OrganisingInstitution.label}
                        maxStringCount={OrganisingInstitution.maxStringCount} disabled={isSpaEditabe} information_text={OrganisingInstitution.information_text} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        Lead organiser details
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={LeadOrganiserFirstName.name} label={LeadOrganiserFirstName.label}
                        maxStringCount={LeadOrganiserFirstName.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={LeadOrganiserSurname.name} label={LeadOrganiserSurname.label}
                        maxStringCount={LeadOrganiserSurname.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                {getValues(OrganiserType.name as keyof PcsEventDto) === EventOrganiserTypeEnum.SurgicalMedicalLeadOrganiser &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={LeadOrganiserIMCNumber.name} label={LeadOrganiserIMCNumber.label}
                            maxStringCount={LeadOrganiserIMCNumber.maxStringCount} disabled={isSpaEditabe} information_text={LeadOrganiserIMCNumber.information_text} />
                    </Grid>
                }

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={LeadOrganiserMobile.name} label={LeadOrganiserMobile.label}
                        maxStringCount={LeadOrganiserMobile.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextInput control={control} name={LeadOrganiserEmail.name} label={LeadOrganiserEmail.label}
                        maxStringCount={LeadOrganiserEmail.maxStringCount} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppTextareaInput control={control} name={LeadOrganiserWorkplaceAddress.name}
                        maxStringCount={LeadOrganiserWorkplaceAddress.maxStringCount}
                        label={LeadOrganiserWorkplaceAddress.label} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        Administration organiser details
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography component="span">
                        The Administrator is the person responsible for co-ordination and logistics,
                        including contacting attendees, registration and issuing of CPD attendance
                        Certificates following the event.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <AppRadioButtonsGroup control={control} name={AreYouEventOrganiser.name}
                        label={AreYouEventOrganiser.label} items={yesNoArray} disabled={isSpaEditabe} />
                </Grid>

                {getValues(AreYouEventOrganiser.name as keyof PcsEventDto) === YesNoEnum.NO && <>
                    <Grid size={{ xs: 12 }}>
                        <Typography component="span">
                            Please complete details of administrator below.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={AdministrationOrganiserDetailsFirstName.name}
                            maxStringCount={AdministrationOrganiserDetailsFirstName.maxStringCount}
                            label={AdministrationOrganiserDetailsFirstName.label} disabled={isSpaEditabe} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={AdministrationOrganiserSurname.name}
                            maxStringCount={AdministrationOrganiserSurname.maxStringCount}
                            label={AdministrationOrganiserSurname.label} disabled={isSpaEditabe} />
                    </Grid>


                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={AdministrationOrganiserMobile.name}
                            maxStringCount={AdministrationOrganiserMobile.maxStringCount}
                            label={AdministrationOrganiserMobile.label} disabled={isSpaEditabe} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={AdministrationOrganiserEmail.name}
                            maxStringCount={AdministrationOrganiserEmail.maxStringCount}
                            label={AdministrationOrganiserEmail.label} disabled={isSpaEditabe} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextareaInput control={control} name={AdministrationOrganiserWorkplaceAddress.name}
                            maxStringCount={AdministrationOrganiserWorkplaceAddress.maxStringCount}
                            label={AdministrationOrganiserWorkplaceAddress.label} disabled={isSpaEditabe} />
                    </Grid>
                </>}

                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext('OrganiserDetails'), 'OrganiserDetails.onClick.handleBack', handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={saveDetails}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>
    </FormProvider >
}
